import React, { useState, useEffect } from "react";
import { useApi } from "../hooks/useApi";
import Card from "./Card";

const formatImpact = (val) => {
    if (val == null) return "\u2014";
    const n = Number(val);
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    return `$${Math.round(n).toLocaleString()}`;
};

const severityColor = (sev) => {
    switch ((sev || "").toUpperCase()) {
        case "CRITICAL": return "bg-red-100 text-red-800";
        case "HIGH": return "bg-orange-100 text-orange-800";
        case "MEDIUM": return "bg-yellow-100 text-yellow-800";
        case "LOW": return "bg-green-100 text-green-800";
        default: return "bg-gray-100 text-gray-800";
    }
};

const VendorSingleIntervention = ({ vendorId, financialSummary }) => {
    const apiCall = useApi();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await apiCall("GET", `/TPRMVendor/${vendorId}/single-intervention`);
                if (!cancelled) setData(result);
            } catch (err) {
                if (!cancelled) setError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadData();
        return () => { cancelled = true; };
    }, [vendorId, apiCall]);

    if (loading) return (
        <Card className="mb-6 border-l-4 border-l-indigo-500">
            <div className="flex items-center justify-center h-16">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-transparent border-t-indigo-500 border-r-indigo-300" />
            </div>
        </Card>
    );
    if (error || !data || !data.topAction) return null;

    const { topAction, topActions, currentAvi, estimatedAviReduction, projectedAvi, totalVendorFindings, uniqueFindings } = data;

    // Compute FAIR financial impact using the same proportional ratio as AVI
    const currentFairEAL = financialSummary?.totalExpectedAnnualLoss;
    const fairRatio = (currentAvi && currentAvi > 0) ? estimatedAviReduction / currentAvi : 0;
    const estimatedFairReduction = currentFairEAL ? currentFairEAL * fairRatio : null;
    const projectedFairEAL = (currentFairEAL != null && estimatedFairReduction != null)
        ? Math.max(0, currentFairEAL - estimatedFairReduction) : null;

    // Derive totalVendorExposure so we can compute per-action FAIR reductions
    const totalVendorExposure = (fairRatio > 0 && topAction.exposureReduction)
        ? topAction.exposureReduction / fairRatio : null;

    const actionFairReduction = (exposure) => {
        if (currentFairEAL == null || totalVendorExposure == null || totalVendorExposure === 0) return null;
        return currentFairEAL * (exposure / totalVendorExposure);
    };


    return (
        <Card className="mb-6 border-l-4 border-l-indigo-500">
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h3 className="text-sm font-semibold text-indigo-700 uppercase tracking-wide mb-1">
                        Top Risk-Reduction Action
                    </h3>
                    <p className="text-xs text-gray-500 mb-1">
                        The single highest-impact action to reduce this vendor's financial exposure, ranked by estimated financial impact reduction. Hours and FTE estimates reflect the skill team required. Implement this action first for the greatest overall risk reduction.
                    </p>
                    <p className="text-xs text-gray-500">
                        {uniqueFindings} unique findings from {totalVendorFindings} total TPRM & external findings
                    </p>
                </div>
                {currentFairEAL != null && currentFairEAL > 0 && currentAvi != null && currentAvi > 0 && (
                    <div className="text-right shrink-0 ml-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Est. Financial Impact (FAIR-Aligned)</div>
                        <div className="relative group/avi">
                            <div className="text-lg font-bold text-emerald-700 flex items-center gap-2 justify-end cursor-default">
                                {formatImpact(currentFairEAL)} &rarr; {formatImpact(projectedFairEAL)}
                                {projectedFairEAL === 0 && (
                                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">full elimination</span>
                                )}
                            </div>
                            <div className="absolute bottom-full right-0 mb-1.5 hidden group-hover/avi:block z-20 w-72 p-2.5 bg-gray-800 text-white text-xs rounded shadow-lg pointer-events-none leading-relaxed text-left">
                                <div className="font-semibold mb-1">How this is calculated</div>
                                <div>EAL reduction = current EAL estimate × (action exposure ÷ total vendor exposure). Uses the same proportional ratio as AVI but applied to the FAIR-Aligned Expected Annual Loss.</div>
                                {projectedFairEAL === 0 && uniqueFindings === 1 && (
                                    <div className="mt-1 text-gray-300">Because this vendor has only 1 unique finding, remediating it eliminates 100% of financial exposure — the ratio is 1.0, so projected EAL drops to $0.</div>
                                )}
                            </div>
                        </div>
                        <div className="text-xs text-emerald-600">
                            est. reduction: {formatImpact(estimatedFairReduction)}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                            Expected Annual Loss estimate
                        </div>
                    </div>
                )}
            </div>

            {/* Top action — prominent */}
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200 mb-3">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {topAction.subActions?.length > 0 ? (
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{topAction.actionName}</span>
                    ) : (
                        <span className="font-semibold text-gray-900">{topAction.actionName}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColor(topAction.severity)}`}>
                        {topAction.severity}
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        {topAction.skillLabel}
                    </span>
                </div>
                {topAction.subActions?.length > 0 && (
                    <ul className="mb-2 space-y-1">
                        {topAction.subActions.slice(0, 3).map((sub, i) => (
                            <li key={i} className="text-sm font-semibold text-gray-900 flex items-start gap-1.5">
                                <span className="text-indigo-400 mt-0.5">&#8226;</span>
                                <span>{sub}</span>
                            </li>
                        ))}
                        {topAction.subActions.length > 3 && (
                            <li className="text-xs text-gray-500 ml-4">
                                +{topAction.subActions.length - 3} more
                            </li>
                        )}
                    </ul>
                )}
                <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                        <span className="text-gray-500">Hours:</span>{" "}
                        <span className="font-medium">{topAction.requiredHours?.toFixed(1)}</span>
                    </div>
                    <div>
                        <span className="relative group/fte cursor-default">
                            <span className="text-gray-500">FTE-months:</span>
                            <span className="absolute bottom-full left-0 mb-1.5 hidden group-hover/fte:block z-20 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg pointer-events-none leading-relaxed">
                                FTE-months = effective hours ÷ 109 productive hours/FTE-month (nominal 160h minus meetings, reviews, and non-coding overhead). "Hours" shows the raw skill-hours estimate; "FTE-months" may aggregate across multiple skill types at the action level, so multiplying FTE-months × 109 gives effective hours — which can differ from the displayed raw hours if multiple skills are involved.
                            </span>
                        </span>{" "}
                        <span className="font-medium">{topAction.requiredFte?.toFixed(2)}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Est. Reduction:</span>{" "}
                        <span className="font-medium text-emerald-700">{formatImpact(actionFairReduction(topAction.exposureReduction))}</span>
                        {" "}<span className="relative group inline-block">
                          <span className="text-xs text-gray-400 cursor-help underline decoration-dotted">what's this?</span>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg text-left pointer-events-none">
                            Estimated financial reduction for this action, calculated as current EAL estimate × (action exposure ÷ total vendor exposure).
                          </div>
                        </span>
                    </div>
                </div>
                {topAction.controlsAddressed > 0 && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600">
                            Fixes: <span className="font-medium text-indigo-700">{topAction.controlsAddressed} control{topAction.controlsAddressed !== 1 ? "s" : ""}</span>
                            {topAction.frameworksAffected?.length > 0 && (
                                <> across <span className="font-medium text-indigo-700">{topAction.frameworksAffected.length} framework{topAction.frameworksAffected.length !== 1 ? "s" : ""}</span></>
                            )}
                        </span>
                        {topAction.frameworksAffected?.map((fw) => (
                            <span key={fw} className="px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700 font-medium">
                                {fw}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Other top actions — condensed list */}
            {topActions.length > 1 && (
                <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Other High-Impact Actions</h4>
                    <div className="space-y-1">
                        {topActions.slice(1).map((action, i) => (
                            <div key={i} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-gray-400 text-xs w-4">#{i + 2}</span>
                                    <span className="truncate text-gray-700">{action.actionName}</span>
                                    {action.subActions?.length > 0 && (
                                        <span className="px-1.5 py-0.5 rounded text-xs shrink-0 bg-gray-100 text-gray-600">
                                            {action.subActions.length} {action.subActions.length === 1 ? "risk" : "risks"}
                                        </span>
                                    )}
                                    <span className={`px-1.5 py-0.5 rounded text-xs shrink-0 ${severityColor(action.severity)}`}>
                                        {action.severity}
                                    </span>
                                </div>
                                <div className="flex items-center gap-4 shrink-0 ml-2 text-xs text-gray-500">
                                    <span>{action.requiredHours?.toFixed(1)}h</span>
                                    <span className="text-emerald-600 font-medium">{formatImpact(actionFairReduction(action.exposureReduction))}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};

export default VendorSingleIntervention;
